import { projectRenameError } from '@/utils/projectName';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import type { AppModel } from '@/models/AppModel';
import type { ProjectDto } from '@/schemas/chip';
import { projectStorageErrorMessage } from '@/utils/projectStorageErrorMessage';
import type { ProjectSort, ProjectSortPreference } from './projectSortPreference';

type ProjectListState =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error'; message: string };

type SavedProjectActions = Pick<
  AppModel,
  | 'listSavedProjects'
  | 'renameSavedProject'
  | 'copySavedProject'
  | 'deleteSavedProject'
  | 'exportSavedProjectFile'
  | 'importSavedProject'
> &
  Partial<Pick<AppModel, 'readSavedProjectLastOpened'>>;

interface ProjectFiles {
  pick(): Promise<ProjectDto | null>;
  download(blob: Blob, name: string): void;
}

export interface AttemptProjectReader {
  readReferences(): Promise<readonly { project: string; mainChipId: string }[]>;
  readIdentity(name: string, signal?: AbortSignal): Promise<string | undefined>;
}

/** One Home lifetime, with project storage and file boundaries supplied by its owner. */
export class StartPageViewModel {
  savedProjects: string[] = [];
  newProjectName = '';
  search = '';
  sort: ProjectSort = 'opened-desc';
  lastOpened: ReadonlyMap<string, number> = new Map();
  createOpen = false;
  editingProject: string | null = null;
  editName = '';
  deleteTarget: string | null = null;
  actionError: Error | undefined;
  projectListState: ProjectListState = { status: 'loading' };
  private mounted = false;
  private listRequest = 0;
  private classification?: AbortController;
  private attemptProjects = new Set<string>();

  get visibleProjects(): string[] {
    return this.savedProjects.filter((name) => !this.attemptProjects.has(name));
  }

  get displayProjects(): string[] {
    const query = this.search.trim().toLocaleLowerCase();
    return this.visibleProjects
      .filter((name) => name.toLocaleLowerCase().includes(query))
      .sort((a, b) => {
        if (this.sort === 'opened-desc') {
          const left = this.lastOpened.get(a),
            right = this.lastOpened.get(b);
          if (left !== undefined && right === undefined) return -1;
          if (left === undefined && right !== undefined) return 1;
          if (left !== undefined && right !== undefined && left !== right)
            return right - left;
        }
        return (
          (this.sort === 'desc' ? -1 : 1) *
          (a.localeCompare(b) || (a < b ? -1 : a > b ? 1 : 0))
        );
      });
  }
  setSearch(value: string) {
    this.search = value;
  }
  setSort(value: ProjectSort) {
    this.sort = value;
    this.sortPreference?.write(value);
  }
  setCreateOpen(value: boolean) {
    this.createOpen = value;
  }

  constructor(
    private readonly projects: SavedProjectActions,
    private readonly files: ProjectFiles,
    private readonly attempts?: AttemptProjectReader,
    private readonly sortPreference?: ProjectSortPreference,
  ) {
    this.sort = sortPreference?.read() ?? 'opened-desc';
    makeAutoObservable<
      this,
      | 'projects'
      | 'files'
      | 'attempts'
      | 'mounted'
      | 'listRequest'
      | 'classification'
      | 'attemptProjects'
      | 'sortPreference'
    >(
      this,
      {
        projects: false,
        files: false,
        attempts: false,
        sortPreference: false,
        lastOpened: observable.ref,
        classification: false,
        attemptProjects: observable.ref,
        mounted: false,
        listRequest: false,
        savedProjects: observable.ref,
        actionError: observable.ref,
        projectListState: observable.ref,
      },
      { autoBind: true },
    );
  }

  mount(): void {
    this.mounted = true;
    void this.refreshProjects();
  }

  unmount(): void {
    this.mounted = false;
    this.classification?.abort();
    ++this.listRequest;
  }

  setNewProjectName(name: string): void {
    this.newProjectName = name;
  }

  startRename(name: string): void {
    this.editingProject = name;
    this.editName = name;
  }

  setEditName(name: string): void {
    this.editName = name;
  }

  cancelRename(): void {
    this.editingProject = null;
  }

  setDeleteTarget(name: string | null): void {
    this.deleteTarget = name;
  }

  async projectAction(action: () => Promise<void>): Promise<void> {
    this.actionError = undefined;
    try {
      await action();
    } catch (error) {
      runInAction(() => {
        this.actionError = new Error(
          projectStorageErrorMessage(error, 'Unable to complete this project action.'),
        );
      });
    }
  }

  async refreshProjects(): Promise<void> {
    if (!this.mounted) return;
    const request = ++this.listRequest;
    this.classification?.abort();
    const classification = new AbortController();
    this.classification = classification;
    this.projectListState = { status: 'loading' };
    try {
      const projects = await this.projects.listSavedProjects();
      if (!this.mounted || request !== this.listRequest) return;
      const hidden = new Set<string>();
      try {
        const references = (await this.attempts?.readReferences()) ?? [];
        const candidates = new Map<string, Set<string>>();
        for (const ref of references) {
          if (!projects.includes(ref.project)) continue;
          const ids = candidates.get(ref.project) ?? new Set<string>();
          ids.add(ref.mainChipId);
          candidates.set(ref.project, ids);
        }
        // Sequential reads bound memory/decoder lifetimes to one candidate.
        for (const [name, ids] of candidates) {
          if (!this.mounted || classification.signal.aborted) return;
          try {
            const id = await this.attempts!.readIdentity(name, classification.signal);
            if (id && ids.has(id)) hidden.add(name);
          } catch {
            /* Uncertain identity stays visible. */
          }
        }
      } catch {
        /* Corrupt/unavailable attempt metadata never hides projects. */
      }
      if (!this.mounted || request !== this.listRequest) return;
      let opened: Record<string, number> = {};
      try {
        opened =
          (await this.projects.readSavedProjectLastOpened?.(
            projects,
            classification.signal,
          )) ?? {};
      } catch {
        // Optional open history never hides projects or blocks the chooser.
      }
      if (!this.mounted || request !== this.listRequest) return;
      runInAction(() => {
        this.attemptProjects = hidden;
        this.lastOpened = new Map(Object.entries(opened));
        this.savedProjects = projects;
        this.projectListState = { status: 'ready' };
      });
    } catch (error) {
      if (!this.mounted || request !== this.listRequest) return;
      runInAction(() => {
        this.projectListState = {
          status: 'error',
          message: projectStorageErrorMessage(error, 'Project storage is unavailable.'),
        };
      });
    }
  }

  async renameProject(oldName: string, newName: string): Promise<void> {
    const trimmed = newName.trim();
    if (trimmed === oldName || projectRenameError(trimmed, oldName, this.savedProjects)) {
      this.editingProject = null;
      return;
    }
    await this.projects.renameSavedProject(oldName, trimmed);
    runInAction(() => {
      this.editingProject = null;
    });
    await this.refreshProjects();
  }

  async copyProject(name: string): Promise<void> {
    let copyName = name + ' - Copy';
    let i = 2;
    while (this.savedProjects.includes(copyName)) {
      copyName = name + ' - Copy ' + i;
      i++;
    }
    await this.projects.copySavedProject(name, copyName);
    await this.refreshProjects();
  }

  async deleteProject(name: string): Promise<void> {
    await this.projects.deleteSavedProject(name);
    runInAction(() => {
      this.deleteTarget = null;
    });
    await this.refreshProjects();
  }

  async exportProject(name: string): Promise<void> {
    const blob = await this.projects.exportSavedProjectFile(name);
    if (blob) this.files.download(blob, name);
  }

  async importProject(): Promise<void> {
    // Match the click-time list captured by the original React handler.
    const knownProjects = this.savedProjects;
    const dto = await this.files.pick();
    if (!dto) return;
    let name = dto.name;
    let i = 2;
    while (knownProjects.includes(name)) {
      name = dto.name + ' (' + i + ')';
      i++;
    }
    dto.name = name;
    await this.projects.importSavedProject(dto);
    await this.refreshProjects();
  }
}
